import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Typography, Button, Stack } from '@mui/material';
import { PictureAsPdf, OpenInNew, Download } from '@mui/icons-material';
import { fetchAuthedBlobUrl, revokeBlobUrl } from '../utils/secureFile';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

/**
 * PDF viewer untuk file backend ber-auth (fetch blob + cookie session).
 * Pakai @react-pdf-viewer (canvas-based) yang work di SEMUA device termasuk mobile.
 */
export default function SecureFilePdf({ src, title = 'PDF Preview', height = 500 }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setError(false);
    setLoading(true);
    setBlobUrl('');

    if (!src) {
      setLoading(false);
      return undefined;
    }

    fetchAuthedBlobUrl(src)
      .then((url) => {
        if (!active) return;
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [src]);

  if (!src) return null;

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <PictureAsPdf sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
        <Typography variant="body2" color="error">Gagal memuat PDF</Typography>
        <Button size="small" href={src} target="_blank" sx={{ mt: 1 }} startIcon={<OpenInNew />}>
          Buka di tab baru
        </Button>
      </Box>
    );
  }

  if (loading || !blobUrl) {
    return (
      <Box sx={{ width: '100%', height }}>
        <Skeleton variant="rectangular" width="100%" height={height} />
      </Box>
    );
  }

  // Render PDF via @react-pdf-viewer (canvas-based, work di mobile + desktop)
  return (
    <Box sx={{ width: '100%', height, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'auto', bgcolor: '#f5f5f5' }}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
        <Viewer fileUrl={blobUrl} />
      </Worker>
      {/* Fallback tombol di bawah viewer */}
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e0e0e0' }}>
        <Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}>
          Buka di Tab Baru
        </Button>
        <Button size="small" startIcon={<Download />} component="a" href={blobUrl} download={`${title || 'document'}.pdf`}>
          Unduh PDF
        </Button>
      </Stack>
    </Box>
  );
}
